/**
 * Public pre-registration data access. Anon-safe: only SECURITY DEFINER RPCs
 * and the event-doc-upload edge function — no direct table access.
 */

import { supabase } from '@/supabase.config';
import { normalizeConditions } from '@/Services/pricingEngine.service';
import type {
  DocumentKind,
  MinisterLookupResult,
  MinisterPrefill,
  PublicEvent,
  PublicRegistration,
  RegistrationEditLink,
  RegistrationEditPayload,
  RegistrationEditResult,
  RegistrationEditView,
  SubmitPreregistrationPayload,
} from './types';

// RPCs added in 20261004000002 aren't in the generated types until regen.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await db.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

export const getPublicEvent = async (code: string): Promise<PublicEvent> => {
  const event = await rpc<PublicEvent>('public_get_event', { p_code: code });
  return {
    ...event,
    // Rules with unreadable conditions are dropped rather than treated as "applies to everyone"
    pricing_rules: (event.pricing_rules ?? []).flatMap((r) => {
      const conditions = normalizeConditions(r.conditions);
      return conditions ? [{ ...r, conditions }] : [];
    }),
  };
};

export interface OpenEventSummary {
  code: string;
  name: string;
  location: string | null;
  start_date: string;
  end_date: string;
  flyer_filename: string | null;
  registration_deadline: string | null;
}

export const listOpenEvents = () => rpc<OpenEventSummary[]>('public_list_open_events', {});

export const lookupMinister = (eventId: string, query: string) =>
  rpc<MinisterLookupResult[]>('public_lookup_minister', { p_event_id: eventId, p_query: query });

export const getMinisterPrefill = (eventId: string, ministerId: string, dni: string) =>
  rpc<MinisterPrefill>('public_get_minister_prefill', {
    p_event_id: eventId,
    p_minister_id: ministerId,
    p_dni: dni,
  });

export const submitPreregistration = (payload: SubmitPreregistrationPayload) =>
  rpc<{ token: string; participant_id: string }>('public_submit_preregistration', { p_payload: payload });

export const getPublicRegistration = (token: string) =>
  rpc<PublicRegistration>('get_public_registration', { p_token: token });

/** Asks the edge function for a presigned PUT, uploads the file and returns the stored key. */
async function uploadViaEdgeFunction(body: Record<string, unknown>, file: Blob): Promise<string> {
  const { data, error } = await supabase.functions.invoke('event-doc-upload', {
    body: { ...body, contentType: file.type, size: file.size },
  });
  if (error) {
    // FunctionsHttpError carries the JSON body in context
    const message = await error.context?.json?.().then((b: { error?: string }) => b.error).catch(() => null);
    throw new Error(message || 'No se pudo preparar la subida del archivo');
  }

  const res = await fetch(data.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!res.ok) throw new Error('Error al subir el archivo');
  return data.key as string;
}

/** Upload a registration document to private storage; returns the stored object key. */
export const uploadRegistrationDocument = (eventId: string, kind: DocumentKind, file: Blob) =>
  uploadViaEdgeFunction({ action: 'upload', eventId, kind }, file);

/** Same, for someone holding a personal edit link (works while the event is OPEN or CLOSED). */
export const uploadEditDocument = (token: string, kind: DocumentKind, file: Blob) =>
  uploadViaEdgeFunction({ action: 'edit-upload', token, kind }, file);

// ===================
// Edit a registration form (ADB-169)
// ===================

/** Participant: the form behind a personal edit link (throws a readable message when the link is invalid or expired). */
export const getRegistrationEdit = (token: string) =>
  rpc<RegistrationEditView>('public_get_registration_edit', { p_token: token });

export const updateRegistrationByLink = (token: string, payload: RegistrationEditPayload) =>
  rpc<RegistrationEditResult>('public_update_registration', { p_token: token, p_payload: payload });

/** Short-lived URL of one of the registration's own documents, for someone holding its edit link. */
export async function getEditDocumentUrl(
  token: string,
  kind: DocumentKind,
): Promise<{ url: string; isPdf: boolean } | null> {
  const { data, error } = await supabase.functions.invoke('event-doc-upload', {
    body: { action: 'edit-view', token, kind },
  });
  if (error) throw new Error('No se pudo cargar el documento');
  return data?.url ? { url: data.url as string, isPdf: !!data.isPdf } : null;
}

/** Staff: the same view, by participant. */
export const adminGetRegistrationEdit = (participantId: string) =>
  rpc<RegistrationEditView>('admin_get_registration_edit', { p_participant_id: participantId });

export const adminUpdateRegistration = (participantId: string, payload: RegistrationEditPayload) =>
  rpc<RegistrationEditResult>('admin_update_registration', { p_participant_id: participantId, p_payload: payload });

export const adminCreateEditLink = (participantId: string, days = 7) =>
  rpc<RegistrationEditLink>('admin_create_registration_edit_link', { p_participant_id: participantId, p_days: days });

export const adminGetEditLink = (participantId: string) =>
  rpc<RegistrationEditLink | null>('admin_get_registration_edit_link', { p_participant_id: participantId });

export const adminRevokeEditLinks = (participantId: string) =>
  rpc<null>('admin_revoke_registration_edit_links', { p_participant_id: participantId });

/** Staff: link an uploaded file to a registration, replacing (or adding) that document. Returns the new documents map. */
export async function replaceRegistrationDocument(
  participantId: string,
  kind: DocumentKind,
  key: string,
): Promise<Partial<Record<DocumentKind, string>>> {
  return rpc<Partial<Record<DocumentKind, string>>>('replace_registration_document', {
    p_participant_id: participantId,
    p_kind: kind,
    p_key: key,
  });
}

/** Short-lived URL of the drawn signature of the registration that owns this public token (null if none). */
export async function getSignatureUrlByToken(token: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('event-doc-upload', {
    body: { action: 'signature', token },
  });
  if (error) throw new Error('No se pudo cargar la firma');
  return (data.url as string | null) ?? null;
}

/** Admin only: short-lived URL to view a stored registration document. */
export async function getRegistrationDocumentUrl(key: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('event-doc-upload', {
    body: { action: 'view', key },
  });
  if (error) throw new Error('No se pudo abrir el documento');
  return data.url as string;
}
